import { makeAutoObservable, observable, autorun, runInAction } from "mobx";
import { toast } from "sonner";
import { UploadFileUseCase } from "@/application/upload/usecases/UploadFileUseCase";
import { ConfirmTextFileUploadUseCase } from "@/application/upload/usecases/ConfirmTextFileUploadUseCase";
import { CancelUploadUseCase } from "@/application/upload/usecases/CancelUploadUseCase";
import { UploadFilePresenter } from "@/application/upload/ports/UploadFilePresenter";
import { UploadTaskDTO } from "@/application/upload/dtos/UploadTaskDTO";
import { TextFileDraftDTO } from "@/application/upload/dtos/TextFileDraftDTO";
import { UploadCompletionDTO } from "@/application/upload/dtos/UploadCompletionDTO";
import { StorageUploadController } from "@/application/upload/ports/StorageUploadService";
import { UploadControllerRegistry } from "@/application/upload/ports/UploadControllerRegistry";
import { ConfirmTextFileUploadRequestDTO } from "@/application/upload/dtos/ConfirmTextFileUploadRequestDTO";
import { ConfirmTextFileUploadResponseDTO } from "@/application/upload/dtos/ConfirmTextFileUploadResponseDTO";
import { ANALYTICS_EVENTS } from "@/lib/analytics/catalog";
import { trackFlow, trackResult, trackUi } from "@/lib/analytics/amplitude";
import { normalizeUploadErrorCode } from "@/lib/analytics/normalizers";
import type { UploadSource } from "@/lib/analytics/types";

const COMPLETED_TASK_VISIBILITY_TIMEOUT = 15000;

function showErrorToast(message: string): void {
  if (message.includes("\n")) {
    const [title, ...rest] = message.split("\n");
    toast.error(title.trim(), { description: rest.join("\n").trim() });
  } else {
    toast.error(message);
  }
}

type PendingUploadMeta = {
  originalFileName: string;
  sizeMb: number;
  uploadSource: UploadSource;
  projectId?: string;
};

type TaskMeta = {
  uploadSource: UploadSource;
  projectId?: string;
  sizeMb?: number;
};

export interface TextFileModalState {
  isVisible: boolean;
  draft: TextFileDraftDTO | null;
}

export type RecordsRefresher = () => Promise<void>;

export class UploadStore implements UploadFilePresenter {
  readonly tasks = observable.map<string, UploadTaskDTO>();
  textFileModal: TextFileModalState = {
    isVisible: false,
    draft: null,
  };
  textFileQueue: TextFileDraftDTO[] = [];
  private readonly pendingTaskMetaQueue: PendingUploadMeta[] = [];
  private readonly taskMetaById = new Map<string, TaskMeta>();
  private readonly taskStatusById = new Map<string, UploadTaskDTO["status"]>();
  private readonly completedTaskCleanupTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  constructor(
    private readonly uploadFileUseCase: UploadFileUseCase,
    private readonly confirmTextFileUploadUseCase: ConfirmTextFileUploadUseCase,
    private readonly cancelUploadUseCase: CancelUploadUseCase,
    private readonly controllerRegistry: UploadControllerRegistry,
    private readonly refreshRecords: RecordsRefresher
  ) {
    makeAutoObservable(this, undefined, { autoBind: true });
  }

  get uploadFileTasks(): Map<string, UploadTaskDTO> {
    return new Map(this.tasks);
  }

  get isUploading(): boolean {
    return Array.from(this.tasks.values()).some(
      (task) => task.status === "uploading"
    );
  }

  getUploadTask(taskId: string): UploadTaskDTO | undefined {
    return this.tasks.get(taskId);
  }

  subscribeToTask(taskId: string, callback: () => void): () => void {
    const disposer = autorun(() => {
      this.tasks.get(taskId);
      callback();
    });

    return () => disposer();
  }

  subscribe(listener: () => void): () => void {
    let isFirstRun = true;
    const disposer = autorun(() => {
      // Track aggregated observables that should trigger provider updates
      this.tasks.forEach((task) => {
        // Intentional MobX tracking expressions - these create reactions
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        task.progress;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        task.status;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        task.error;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        task.sizeMb;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        task.durationSec;
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        task.placeholderRecord;
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.tasks.size;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.textFileModal.isVisible;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.textFileModal.draft;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.textFileQueue.length;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.isUploading;

      if (isFirstRun) {
        isFirstRun = false;
        return;
      }

      listener();
    });

    return () => disposer();
  }

  async uploadFile(
    file: File,
    projectId: string | undefined,
    uploadSource: UploadSource
  ): Promise<void> {
    const sizeMb = Number((file.size / (1024 * 1024)).toFixed(2));
    this.pendingTaskMetaQueue.push({
      originalFileName: file.name,
      sizeMb,
      uploadSource,
      projectId,
    });

    try {
      await this.uploadFileUseCase.execute({ file, projectId, uploadSource }, this);
    } catch (error) {
      this.removePendingTaskMeta(file.name, sizeMb, uploadSource, projectId);
      const message =
        error instanceof Error ? error.message : "Upload failed to start";
      showErrorToast(message);
    }
  }

  async uploadMultipleFiles(
    files: File[],
    projectId: string | undefined,
    uploadSource: UploadSource
  ): Promise<void> {
    for (const file of files) {
      // NOTE: Sequential execution is intentional here
      // We process files one at a time to maintain proper queue order for text files
      // and prevent race conditions. This is a deliberate design choice.
      await this.uploadFile(file, projectId, uploadSource);
    }
  }

  async confirmTextFileUpload(payload: {
    record_name: string;
    content: string;
    file_extension: "txt" | "md" | "srt" | "vtt";
    transcript_format?:
      | "speaker-text"
      | "timed-subtitles"
      | "tab-separated"
      | "plain-text"
      | "aseed"
      | "datetime-speaker";
    upload_source?: UploadSource;
  }): Promise<void> {
    if (!this.textFileModal.draft) {
      return;
    }

    const { draft } = this.textFileModal;
    const request: ConfirmTextFileUploadRequestDTO = {
      taskId: draft.taskId,
      projectId: draft.projectId,
      recordName: payload.record_name ?? draft.name,
      content: payload.content,
      extension: payload.file_extension,
      transcriptFormat: payload.transcript_format,
    };

    this.updateTask(draft.taskId, (task) => ({
      ...task,
      status: "uploading",
      progress: 0,
    }));

    try {
      const response = await this.confirmTextFileUploadUseCase.execute(request);
      await this.handleTextUploadCompleted(response);
      trackResult(ANALYTICS_EVENTS.RESULT_TEXT_TRANSCRIPT_UPLOAD_SUCCESS, {
        task_id: draft.taskId,
        project_id: draft.projectId,
        upload_source: payload.upload_source ?? draft.uploadSource ?? "unknown",
        transcript_format: payload.transcript_format ?? "speaker-text",
        file_extension: payload.file_extension,
      });
      toast.success("Text file uploaded");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to upload text file";
      const errorCode = normalizeUploadErrorCode(error);
      this.updateTask(draft.taskId, (task) => ({
        ...task,
        status: "error",
        error: message,
      }));
      const task = this.tasks.get(draft.taskId);
      const meta = this.getTaskMeta(draft.taskId);
      trackResult(ANALYTICS_EVENTS.RESULT_UPLOAD_TASK_FAILED, {
        task_id: draft.taskId,
        upload_source: meta.uploadSource,
        project_id: meta.projectId,
        file_type: task?.type ?? "text",
        file_extension: payload.file_extension,
        size_mb: task?.sizeMb ?? meta.sizeMb,
        error_code: errorCode,
      });
      trackResult(ANALYTICS_EVENTS.RESULT_TEXT_TRANSCRIPT_UPLOAD_FAILED, {
        task_id: draft.taskId,
        project_id: draft.projectId,
        upload_source: payload.upload_source ?? draft.uploadSource ?? "unknown",
        transcript_format: payload.transcript_format ?? "speaker-text",
        file_extension: payload.file_extension,
        error_code: errorCode,
      });
      showErrorToast(message);
    } finally {
      this.clearController(draft.taskId);
      this.removeTaskFromQueue(draft.taskId);
      this.hideTextFileModalAfterSuccess();
    }
  }

  hideTextFileModal(): void {
    const currentDraft = this.textFileModal.draft;
    if (currentDraft) {
      this.removeTask(currentDraft.taskId);
    }
    this.textFileModal = {
      isVisible: false,
      draft: null,
    };
    this.processNextTextDraft();
  }

  hideTextFileModalAfterSuccess(): void {
    this.textFileModal = {
      isVisible: false,
      draft: null,
    };
    this.processNextTextDraft();
  }

  cancelUpload(taskId: string): void {
    const task = this.tasks.get(taskId);
    const meta = this.getTaskMeta(taskId);
    trackUi(ANALYTICS_EVENTS.UI_UPLOAD_TASK_CANCEL_REQUESTED, {
      task_id: taskId,
      upload_source: meta.uploadSource,
      project_id: meta.projectId,
      file_type: task?.type,
      file_extension: this.getFileExtension(task?.originalFileName),
      size_mb: task?.sizeMb,
    });

    this.cancelUploadUseCase.execute(taskId);
    this.clearController(taskId);
    this.updateTask(taskId, (task) => ({
      ...task,
      status: "cancelled",
      error: undefined,
    }));
    this.scheduleTaskRemoval(taskId);
    toast.success("Upload cancelled");
  }

  removeTask(taskId: string): void {
    this.clearController(taskId);
    this.clearScheduledRemoval(taskId);
    this.tasks.delete(taskId);
    this.taskMetaById.delete(taskId);
    this.taskStatusById.delete(taskId);
  }

  addUploadedFile(task: UploadTaskDTO): void {
    this.taskStatusById.set(task.id, task.status);
    if (!this.taskMetaById.has(task.id)) {
      this.taskMetaById.set(task.id, { uploadSource: "unknown" });
    }
    this.tasks.set(task.id, task);
  }

  // Presenter implementations
  presentTaskCreated(task: UploadTaskDTO): void {
    const pendingIndex = this.pendingTaskMetaQueue.findIndex(
      (meta) => meta.originalFileName === task.originalFileName
    );
    const pendingMeta =
      pendingIndex >= 0
        ? this.pendingTaskMetaQueue.splice(pendingIndex, 1)[0]
        : null;
    this.taskMetaById.set(task.id, {
      uploadSource: pendingMeta?.uploadSource ?? "unknown",
      projectId: pendingMeta?.projectId,
      sizeMb: pendingMeta?.sizeMb,
    });
    this.taskStatusById.set(task.id, task.status);
    this.tasks.set(task.id, task);
  }

  presentTaskUpdated(task: UploadTaskDTO): void {
    const previousStatus = this.taskStatusById.get(task.id);
    this.tasks.set(task.id, task);
    this.taskStatusById.set(task.id, task.status);
    this.trackTaskStatusTransition(task, previousStatus);
  }

  presentUploadProgress(taskId: string, progress: number): void {
    this.updateTask(taskId, (task) => ({
      ...task,
      progress,
    }));
  }

  presentTextFileDraft(draft: TextFileDraftDTO): void {
    if (this.textFileModal.isVisible) {
      this.textFileQueue.push(draft);
      return;
    }

    this.textFileModal = {
      isVisible: true,
      draft,
    };
  }

  presentUploadController(taskId: string, controller: StorageUploadController) {
    this.controllerRegistry.register(taskId, controller);
  }

  presentUploadCompleted(payload: UploadCompletionDTO): void {
    this.clearController(payload.taskId);
    this.updateTask(payload.taskId, (task) => ({
      ...task,
      status: "completed",
      progress: 100,
      sizeMb: payload.sizeMb,
      durationSec: payload.durationSec,
      placeholderRecord: payload.placeholderRecord,
    }));
    this.scheduleTaskRemoval(payload.taskId);
    void this.refreshAfterUpload();
  }

  presentUploadError(taskId: string, message: string): void {
    this.clearController(taskId);
    const errorCode = normalizeUploadErrorCode(message);
    this.updateTask(taskId, (task) => ({
      ...task,
      status: "error",
      error: message,
    }));
    const task = this.tasks.get(taskId);
    const meta = this.getTaskMeta(taskId);
    trackResult(ANALYTICS_EVENTS.RESULT_UPLOAD_TASK_FAILED, {
      task_id: taskId,
      upload_source: meta.uploadSource,
      project_id: meta.projectId,
      file_type: task?.type,
      file_extension: this.getFileExtension(task?.originalFileName),
      size_mb: task?.sizeMb,
      error_code: errorCode,
    });
    showErrorToast(message);
  }

  presentUploadCancelled(taskId: string): void {
    this.clearController(taskId);
    this.updateTask(taskId, (task) => ({
      ...task,
      status: "cancelled",
    }));
    this.scheduleTaskRemoval(taskId);
  }

  private updateTask(
    taskId: string,
    updater: (task: UploadTaskDTO) => UploadTaskDTO
  ): void {
    const current = this.tasks.get(taskId);
    if (!current) {
      return;
    }
    const nextTask = updater(current);
    this.tasks.set(taskId, nextTask);
    this.taskStatusById.set(taskId, nextTask.status);
    this.trackTaskStatusTransition(nextTask, current.status);
  }

  private scheduleTaskRemoval(taskId: string): void {
    this.clearScheduledRemoval(taskId);
    const handle = setTimeout(() => {
      runInAction(() => {
        this.tasks.delete(taskId);
        this.taskMetaById.delete(taskId);
        this.taskStatusById.delete(taskId);
        this.completedTaskCleanupTimers.delete(taskId);
      });
    }, COMPLETED_TASK_VISIBILITY_TIMEOUT);
    this.completedTaskCleanupTimers.set(taskId, handle);
  }

  private clearScheduledRemoval(taskId: string): void {
    const timer = this.completedTaskCleanupTimers.get(taskId);
    if (timer) {
      clearTimeout(timer);
      this.completedTaskCleanupTimers.delete(taskId);
    }
  }

  private async handleTextUploadCompleted(
    response: ConfirmTextFileUploadResponseDTO
  ): Promise<void> {
    this.updateTask(response.taskId, (task) => ({
      ...task,
      status: "completed",
      progress: 100,
      placeholderRecord: response.placeholderRecord,
    }));
    this.scheduleTaskRemoval(response.taskId);
    await this.refreshAfterUpload();
  }

  private removeTaskFromQueue(taskId: string): void {
    this.textFileQueue = this.textFileQueue.filter(
      (draft) => draft.taskId !== taskId
    );
  }

  private processNextTextDraft(): void {
    if (this.textFileQueue.length === 0) {
      return;
    }
    const [nextDraft, ...rest] = this.textFileQueue;
    this.textFileQueue = rest;
    this.textFileModal = {
      isVisible: true,
      draft: nextDraft,
    };
  }

  private clearController(taskId: string): void {
    this.controllerRegistry.remove(taskId);
  }

  private getTaskMeta(taskId: string): TaskMeta {
    const meta = this.taskMetaById.get(taskId);
    if (!meta) {
      return {
        uploadSource: "unknown",
      };
    }
    return meta;
  }

  private getFileExtension(fileName?: string): string | undefined {
    if (!fileName) {
      return undefined;
    }
    const segments = fileName.split(".");
    if (segments.length < 2) {
      return undefined;
    }
    return segments[segments.length - 1]?.toLowerCase();
  }

  private trackTaskStatusTransition(
    task: UploadTaskDTO,
    previousStatus?: UploadTaskDTO["status"]
  ): void {
    if (previousStatus === task.status) {
      return;
    }

    const meta = this.getTaskMeta(task.id);
    const sharedProps = {
      task_id: task.id,
      upload_source: meta.uploadSource,
      project_id: meta.projectId,
      file_type: task.type,
      file_extension: this.getFileExtension(task.originalFileName),
      size_mb: task.sizeMb ?? meta.sizeMb,
    };

    if (task.status === "uploading") {
      trackFlow(ANALYTICS_EVENTS.FLOW_UPLOAD_TASK_STARTED, sharedProps);
      return;
    }

    if (task.status === "completed") {
      trackResult(ANALYTICS_EVENTS.RESULT_UPLOAD_TASK_SUCCESS, sharedProps);
      return;
    }

    if (task.status === "cancelled") {
      trackResult(ANALYTICS_EVENTS.RESULT_UPLOAD_TASK_CANCELLED, {
        ...sharedProps,
        error_code: "upload_aborted",
      });
    }
  }

  private removePendingTaskMeta(
    fileName: string,
    sizeMb: number,
    uploadSource: UploadSource,
    projectId?: string
  ): void {
    const pendingIndex = this.pendingTaskMetaQueue.findIndex(
      (meta) =>
        meta.originalFileName === fileName &&
        meta.sizeMb === sizeMb &&
        meta.uploadSource === uploadSource &&
        meta.projectId === projectId
    );
    if (pendingIndex >= 0) {
      this.pendingTaskMetaQueue.splice(pendingIndex, 1);
    }
  }

  private async refreshAfterUpload(): Promise<void> {
    try {
      await this.refreshRecords();
    } catch (error) {
      console.error("Failed to refresh records after upload:", error);
    }
  }
}
